/**
 * Canonical NextCompli subscription tier definitions.
 * Competitive-anchor naming: CompliCore / CompliAdvantage / CompliComplete.
 * Internal tier ids (basic/advanced/enterprise) remain stable for routes and entitlements.
 */

export type PricingTierId = 'basic' | 'advanced' | 'enterprise';

export type PricingTier = {
  id: PricingTierId;
  /** Competitive-anchor display name */
  name: string;
  /** Internal job-to-be-done codename */
  codename: string;
  marketingLabel: string;
  priceDisplay: string | null;
  priceMonthlyUsd: number | null;
  cadence: string | null;
  seats: string;
  /** People included on this company. Null means no people limit. */
  includedLicenses: number | null;
  ctaLabel: string;
  subtitle: string;
  includes: string[];
  idealFor: string;
  expansionSummary: string;
  expansionPoints: string[];
  graphicPath: string;
  graphicAlt: string;
  featured?: boolean;
  supportRouteId: string;
  usagePricingSubSlug: string;
  legacyAliases: string[];
  featureEntitlements: string[];
};

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'basic',
    codename: 'Screen',
    name: 'CompliCore',
    marketingLabel: 'CompliCore',
    priceDisplay: '$99',
    priceMonthlyUsd: 99,
    cadence: 'starting per month (USD)',
    seats: '10 people included',
    includedLicenses: 10,
    ctaLabel: 'Start CompliCore',
    subtitle: 'Core compliance controls for teams launching formal screening operations.',
    includes: [
      'OFAC Compliance',
      'Website Compliance',
      'Cloud Compliance checklist',
      'Government Compliance',
      'Education Compliance',
      'AI Compliance',
      'Dashboards with Data Source Health Monitoring',
      'Risk-scoring on screening results',
      'Research Console',
      'Razzmatazz studio workflow',
    ],
    idealFor: 'Best for teams standing up formal screening without heavy process overhead.',
    expansionSummary: 'Core sanctions, website, government, and education workflows in one starter package.',
    expansionPoints: [
      'OFAC on this plan is named-party onboard (customers, vendors, payees, hires) plus operator Rescreen book after Treasury list notices. Continuous monitoring and CSV/payment batch are CompliAdvantage.',
      'Baseline controls for sanctions checks and reporting hygiene.',
      'Website Compliance includes email authentication (SPF that matches the mail host, one DKIM key, DMARC with a report mailbox) and essential Zero Trust hygiene for cloud, containers, git, and the public site.',
      'Cloud Compliance on this plan is the written checklist: sign-in, audit logs, network exposure, encryption, backups, and findings. The once-a-day read-only review is CompliAdvantage.',
      'Government Compliance on this plan is FCI / CMMC Level 1 plus local, county, state, and federal vendor lists: entity, SAM, insurance, 15 FAR 52.204-21 practices, and SPRS affirmation. No CUI in the SaaS. SBIR and STTR phases are recorded there. NextCompli does not submit the proposal.',
      'AI Compliance on this plan is the tool inventory, the one-page use rule, and a named owner. It does not certify NIST AI RMF or ISO/IEC 42001.',
      'Razzmatazz on this plan is the studio workflow: brief, pages, phrases, a draft, a person’s yes, a live mark, and a weekly recap. Phrase watches, AI-answer watches, link prospects, and a repeating recap are CompliAdvantage.',
      'Faster time-to-value at entry pricing.',
    ],
    graphicPath: '/plans/20160316_BasicPackage.webp?v=20260316t',
    graphicAlt: 'CompliCore package illustration with sanctions screening, dashboards, and research console',
    supportRouteId: 'support-basic',
    usagePricingSubSlug: 'basic-starting-at-99-month',
    legacyAliases: ['Essentials', 'Basic', 'Starter', 'Starter Support', 'plan-starter', 'Basic (Screen)'],
    featureEntitlements: [
      'usage-based-pricing-plans',
      'ofac-compliance',
      'website-compliance',
      'cloud-compliance',
      'dod-contracts-compliance',
      'education-compliance',
      'ai-compliance',
      'business-creation-compliance',
      'wills-trusts-compliance',
      'dashboards',
      'research-reports',
    ],
  },
  {
    id: 'advanced',
    codename: 'Monitor',
    name: 'CompliAdvantage',
    marketingLabel: 'CompliAdvantage',
    priceDisplay: '$999',
    priceMonthlyUsd: 999,
    cadence: 'starting per month (USD)',
    seats: '50 people included',
    includedLicenses: 50,
    ctaLabel: 'Start CompliAdvantage',
    subtitle: 'Expanded controls with continuous monitoring and incident workflow automation.',
    includes: [
      'Everything in CompliCore',
      'Live Environment Monitoring',
      'Real-time + Batch Screening',
      'Audit Trail + Alert Workflow',
      'Alerts & Incident Workflow with priority pipelines',
      'Incident response actions',
      'Razzmatazz watches',
    ],
    idealFor: 'Best for teams scaling case volume with continuous monitoring.',
    expansionSummary: 'Adds real-time/batch screening, alert routing, and incident automation.',
    expansionPoints: [
      'OFAC monitoring: automatic book rescreen when Treasury mail kicks a list refresh, CSV / payment-party batch before funds move, and a 10-business-day block/reject reporting clock.',
      'Priority alert pipelines with clearer ownership.',
      'Website Compliance monitoring reads the public site. It does not open the cloud account behind that site.',
      'Cloud Compliance adds a once-a-day review of the client account. The Monitor Agent drafts findings; on Cloud Compliance → Agent gates, subscription admins can allow an approved draft to mark checklist steps or stage evidence. It still does not change the cloud account or use the NextCompli operations account.',
      'Government Compliance adds the CMMC Level 2 tracker, POA&M workspace, and DFARS 252.204-7012 72-hour incident playbook. On Government Compliance → Agent gates, an approved draft can queue portal filing under vaulted customer credentials. CUI staging stays client-scoped and never runs unattended.',
      'AI Compliance adds vendor terms, a person reviewing the output before a decision, a way to stop a bad system, and a quarterly inventory review.',
      'Razzmatazz watches: where the phrases sit, whether an AI answer cites you, link prospects, and a recap that repeats. A person still approves anything marked live.',
      'Higher throughput without losing auditability.',
    ],
    graphicPath: '/plans/advanced-package-mscopilot.webp?v=20260316m',
    graphicAlt: 'CompliAdvantage package: Live Environment Monitoring, batch screening, Real-Time Alerts',
    featured: true,
    supportRouteId: 'support-advanced',
    usagePricingSubSlug: 'advanced-starting-at-999-month',
    legacyAliases: ['Advanced', 'Growth', 'Scale', 'plan-growth', 'Advanced (Monitor)', 'Professional'],
    featureEntitlements: [
      'usage-based-pricing-plans',
      'ofac-compliance',
      'website-compliance',
      'cloud-compliance',
      'dod-contracts-compliance',
      'education-compliance',
      'ai-compliance',
      'business-creation-compliance',
      'wills-trusts-compliance',
      'dashboards',
      'research-reports',
      'continuous-monitoring',
      'real-time-batch-screening',
      'audit-trail-alert-workflow',
      'real-time-alerts',
    ],
  },
  {
    id: 'enterprise',
    codename: 'Govern',
    name: 'CompliComplete',
    marketingLabel: 'CompliComplete',
    priceDisplay: null,
    priceMonthlyUsd: null,
    cadence: null,
    seats: 'Unlimited people',
    includedLicenses: null,
    ctaLabel: 'Contact for estimate',
    subtitle: 'Governance with embedded consultants, executive reporting, and a client-branded program name.',
    includes: [
      'Everything in CompliAdvantage',
      'Embedded Consultants',
      'Executive Compliance Reporting',
      'Liability Compliance Monitoring',
      'Audit Evidence Management',
      'Case & Audit Workspace',
      'Client-branded program name',
    ],
    idealFor: 'Best for regulated orgs needing executive oversight and custom governance.',
    expansionSummary: 'Platform plus embedded consultants, board-ready reporting, and branded program naming.',
    expansionPoints: [
      'OFAC governance: tested sanctions-program evidence, executive reporting, and case/audit workspace. Blocking property and filing remain the client’s duty.',
      'Advisory support for rollout and operating model.',
      'Website Compliance governance: DMARC reject, BIMI, CSPM, signed images, git SSO, and ASVS-depth reviews with embedded consultants. The public-site crawl does not open the cloud account.',
      'Cloud Compliance includes the CompliAdvantage once-a-day read-only review. It writes findings. It does not change the account or use the NextCompli operations account.',
      'Government Compliance adds SSP export, GCC High/Entra/Intune pointers, a PreVeil CUI mailbox pointer, and an optional FedRAMP Moderate enclave. NextCompli does not certify the customer.',
      'AI Compliance adds an impact note when a system affects a person, and the federal and European Union scope questions. It still does not issue a certificate.',
      'Razzmatazz on this plan adds the governed layer: consent, who to leave out, a contact cap, a named audience, a written path, a score with a reason, a handoff to Pursuit, a plain explanation of the result, a sending-domain check, topic choices, a named test, duplicate names, and the film desk. A person still marks each step. Nothing sends itself.',
      'Custom scope, controls, and service alignment.',
    ],
    graphicPath: '/plans/enterprise-package-mscopilot.webp?v=20260316l',
    graphicAlt: 'CompliComplete package: embedded consultants, executive reporting, scheduled audits',
    supportRouteId: 'support-enterprise',
    usagePricingSubSlug: 'enterprise-contact-for-estimate',
    legacyAliases: ['Enterprise', 'Enterprise (Govern)', 'CompliGovern'],
    featureEntitlements: [
      'usage-based-pricing-plans',
      'ofac-compliance',
      'website-compliance',
      'cloud-compliance',
      'dod-contracts-compliance',
      'education-compliance',
      'ai-compliance',
      'business-creation-compliance',
      'wills-trusts-compliance',
      'dashboards',
      'research-reports',
      'continuous-monitoring',
      'real-time-batch-screening',
      'audit-trail-alert-workflow',
      'real-time-alerts',
      'embedded-consultants',
      'executive-compliance-reporting',
      'scheduled-audits',
      'case-audit-workspace',
    ],
  },
];

export const PRICING_TIER_BY_ID: Record<PricingTierId, PricingTier> = PRICING_TIERS.reduce(
  (acc, tier) => {
    acc[tier.id] = tier;
    return acc;
  },
  {} as Record<PricingTierId, PricingTier>,
);

export const getPricingTier = (id: PricingTierId): PricingTier => PRICING_TIER_BY_ID[id];

export const ESSENTIALS_TIER_ID: PricingTierId = 'basic';
export const COMPLICORE_TIER_ID: PricingTierId = ESSENTIALS_TIER_ID;
export const ESSENTIALS_TIER = getPricingTier(ESSENTIALS_TIER_ID);
export const COMPLICORE_TIER = ESSENTIALS_TIER;

export const formatTierPriceSubtitle = (tier: PricingTier): string => {
  if (tier.priceMonthlyUsd == null) {
    return 'Contact for estimate';
  }
  return `Starting at $${tier.priceMonthlyUsd}/month`;
};

export const SUPPORT_LEVELS = PRICING_TIERS.map((tier) => ({
  id: tier.id,
  name: tier.name,
  subtitle: tier.subtitle,
  graphicPath: tier.graphicPath,
  graphicAlt: tier.graphicAlt,
  price: tier.priceDisplay,
  cadence: tier.cadence,
  seats: tier.seats,
  ctaLabel: tier.ctaLabel,
  includes: tier.includes,
  ...(tier.featured ? { featured: true } : {}),
}));

export const SUPPORT_LEVEL_DETAILS: Record<
  string,
  { idealFor: string; expansionSummary: string; expansionPoints: string[] }
> = PRICING_TIERS.reduce(
  (acc, tier) => {
    acc[tier.name] = {
      idealFor: tier.idealFor,
      expansionSummary: tier.expansionSummary,
      expansionPoints: tier.expansionPoints,
    };
    return acc;
  },
  {} as Record<string, { idealFor: string; expansionSummary: string; expansionPoints: string[] }>,
);

export const PLAN_CATEGORY_TABS = PRICING_TIERS.map((tier) => ({
  title: tier.name,
  subtitle: formatTierPriceSubtitle(tier),
}));

export type PaymentPlanRecord = {
  id: string;
  name: string;
  monthlyPrice: string;
  description: string;
  isCurrent: boolean;
  tierId: PricingTierId;
};

export const DEFAULT_PAYMENT_PLANS: PaymentPlanRecord[] = PRICING_TIERS.map((tier) => ({
  id: `plan-${tier.id}`,
  name: tier.marketingLabel,
  monthlyPrice: tier.priceMonthlyUsd == null ? 'Custom' : `$${tier.priceMonthlyUsd}/mo`,
  description: tier.subtitle,
  isCurrent: tier.id === ESSENTIALS_TIER_ID,
  tierId: tier.id,
}));

export const ESSENTIALS_TIER_FEATURE_ENTITLEMENTS = ESSENTIALS_TIER.featureEntitlements;
/** @deprecated Use ESSENTIALS_TIER_FEATURE_ENTITLEMENTS */
export const BASIC_TIER_FEATURE_ENTITLEMENTS = ESSENTIALS_TIER_FEATURE_ENTITLEMENTS;

/**
 * Infer the highest marketing tier whose entitlements are fully granted.
 * Used when Stripe subscription rows are missing but company features are set (e.g. seeded CompliCore).
 */
export const inferEntitledTierId = (features: string[] | null | undefined): PricingTierId | null => {
  const granted = new Set(
    (features || []).map((feature) => String(feature || '').trim().toLowerCase()).filter(Boolean),
  );
  if (granted.size === 0) {
    return null;
  }
  const ordered = [...PRICING_TIERS].reverse();
  for (const tier of ordered) {
    const required = tier.featureEntitlements
      .map((feature) => feature.toLowerCase())
      .filter((feature) => feature !== 'usage-based-pricing-plans');
    if (required.length > 0 && required.every((feature) => granted.has(feature))) {
      return tier.id;
    }
  }
  if (
    granted.has('ofac-compliance')
    || granted.has('website-compliance')
    || granted.has('dod-contracts-compliance')
    || granted.has('education-compliance')
    || granted.has('business-creation-compliance')
    || granted.has('research-reports')
    || granted.has('dashboards')
  ) {
    return 'basic';
  }
  return null;
};
